/**
 * Citations for each `sourceTable` value.
 *
 * A row says WHERE its membership claim comes from; this says where the reader
 * can go and check. Provenance that cannot be followed is decoration, so every
 * value below either carries its own sources or declares that the citation
 * lives on the row itself.
 *
 * ⚠️ All URLs confirmed to resolve on 2026-08-10. EUR-Lex answers automated
 * requests with 202 rather than 200; both CELEX links were confirmed live.
 */
import type { Instrument, SourceTable } from "./types";

export interface SourceCitation {
  /** Full description, used where there is room to be precise. */
  label: string;
  /** Compact form for a table cell in the country index. */
  shortLabel: string;
  /** Empty only when `perRow` is true. */
  sources: Instrument[];
  /**
   * True where there is no single table — the citation is the row's own
   * `statuteUrl`. Only `national-statute` qualifies.
   */
  perRow?: boolean;
}

export const SOURCE_TABLE_CITATIONS: Record<SourceTable, SourceCitation> = {
  "uncitral-mlec": {
    label: "UNCITRAL Model Law on Electronic Commerce (1996) — status table",
    shortLabel: "UNCITRAL MLEC table",
    sources: [
      {
        name: "UNCITRAL MLEC status table",
        url: "https://uncitral.un.org/en/texts/ecommerce/modellaw/electronic_commerce/status",
        note: "88 States, 171 jurisdictions. This registry lists States only.",
      },
    ],
  },
  "uncitral-mles": {
    label: "UNCITRAL Model Law on Electronic Signatures (2001) — status table",
    shortLabel: "UNCITRAL MLES table",
    sources: [
      {
        name: "UNCITRAL MLES status table",
        url: "https://uncitral.un.org/en/texts/ecommerce/modellaw/electronic_signatures/status",
        note: "40 States, 42 jurisdictions.",
      },
    ],
  },
  "eu-eea": {
    label: "EU or EEA membership — eIDAS applies directly",
    shortLabel: "EU/EEA membership",
    sources: [
      {
        name: "EU member countries",
        url: "https://european-union.europa.eu/principles-countries-history/eu-countries_en",
      },
      {
        name: "Agreement on the European Economic Area",
        url: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:21994A0103(01)",
        note: "Extends the Regulation to Iceland, Liechtenstein and Norway.",
      },
    ],
  },
  "national-statute": {
    label: "The national statute itself",
    shortLabel: "National statute",
    sources: [],
    perRow: true,
  },
};

export function citationFor(source: SourceTable): SourceCitation {
  return SOURCE_TABLE_CITATIONS[source];
}
